import {
    OperationVariables,
    QueryLazyOptions,
    useApolloClient,
    useReactiveVar,
} from '@apollo/client'
import { ProductListingDetailsAutoSuggestQuery } from 'bpm/api/graphQL/queries/productSearch.queries'
import { ProductListingDetailsAutoSuggestResult } from 'bpm/api/graphQL/types/product.types'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { stripDoubleSpacesTrimStart } from 'bpm/shared/utils/inputUtils'
import clsx from 'clsx'
import { Typography, debounce } from '@material-ui/core'
import React, {
    ChangeEvent,
    FC,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react'
import { processEnv } from 'components/configuration/environmentVariables'
import { validateFamilyNumber } from '../components/common'
import { RadioType } from './AddProductTypes'
import { AutoCompleteInput } from './AutoCompleteInput'
import {
    addProductDialogState,
    setProductDialogState,
} from './addProductFormHandler'
import { useAddProductFormStyles } from './formStyles'

const isBadgelessCardsEnabled =
    processEnv.REACT_APP_EnableBadgelessCards === '1'

type Props = {
    callAutoSuggest: (
        options?: QueryLazyOptions<OperationVariables> | undefined
    ) => void
    autoSuggestData: any
    autoSuggestLoading: boolean
}

const ExistingFamilyAutoCompleteInput: FC<Props> = ({
    callAutoSuggest,
    autoSuggestData,
    autoSuggestLoading,
}) => {
    const classes = useAddProductFormStyles()
    const client = useApolloClient()

    const [searchText, setSearchText] = useState('')
    const [isOpen, setIsOpen] = useState(false)

    const { firmName, errors, sectionNames, type } = useReactiveVar(
        addProductDialogState
    )

    const callAutoSuggestQuery = useCallback(
        (fetchString?: string) => {
            if (!!type) {
                callAutoSuggest({
                    variables: {
                        input: {
                            listingType: type,
                            familyName: fetchString,
                            productType: '',
                            isProductTypeSearch: false,
                            limit: 50,
                        },
                    },
                })
            }
        },
        [type, searchText, callAutoSuggest]
    )

    const callAutoSuggestQueryRef = useRef(callAutoSuggestQuery)
    callAutoSuggestQueryRef.current = callAutoSuggestQuery

    const debouncedCallAutoSuggestQuery = useMemo(
        () =>
            debounce(
                (value: string) => callAutoSuggestQueryRef.current(value),
                300
            ),
        []
    )

    const sectionAutoSuggestOptions = useMemo(() => {
        const options =
            autoSuggestData?.productListingDetailsAutoSuggest?.productFamilyDetails
                .reduce((acc: string[], curr: Record<string, string>) => {
                    const isMO = new RegExp(/\+/, 'g')
                    if (!!isMO.test(curr.formattedSectionName)) {
                        acc.push(
                            `(+) ${curr.sectionNumber} - ${curr.sectionName}`
                        )
                    } else {
                        acc.push(curr.formattedSectionName)
                    }
                    return acc
                }, [])
                .slice(0, 50) ?? []

        if (
            sectionNames &&
            !options.some((o: string) => o.replace('(+) ', '') === sectionNames)
        ) {
            return [sectionNames, ...options]
        }
        return options
    }, [autoSuggestData, sectionNames])

    const handleCreateNewFamily = useCallback(async () => {
        if (!searchText) return
        const upperValue = searchText.trim().toUpperCase()
        const sectionNum = upperValue.split(/[\s-]/)[0]
        if (!validateFamilyNumber(sectionNum)) {
            setProductDialogState({
                errors: { ...errors, sectionNames: 'Invalid Section Number' },
            })
            return
        }
        const result = await client.query<
            ProductListingDetailsAutoSuggestResult
        >({
            query: ProductListingDetailsAutoSuggestQuery,
            variables: {
                input: {
                    listingType: type,
                    familyName: sectionNum,
                    productType: '',
                    isProductTypeSearch: false,
                    limit: 50,
                },
            },
            fetchPolicy: 'cache-first',
        })
        const fetched =
            result.data?.productListingDetailsAutoSuggest
                ?.productFamilyDetails ?? []
        const sectionExists = fetched.some(
            (o) => o.sectionNumber === sectionNum
        )
        if (sectionExists) {
            setProductDialogState({
                errors: {
                    ...errors,
                    sectionNames: 'Section Number already exists',
                },
            })
            return
        }
        let storedValue: string
        if (upperValue.includes('-')) {
            storedValue = upperValue.replace(/\s*-\s*/, ' - ')
        } else {
            const spaceIdx = upperValue.search(/\s/)
            storedValue =
                spaceIdx !== -1
                    ? `${upperValue.slice(0, spaceIdx)} - ${upperValue
                          .slice(spaceIdx)
                          .trim()}`
                    : `${upperValue} - `
        }
        const titlePart = storedValue.split(' - ').slice(1).join(' - ').trim()
        if (!titlePart) {
            setProductDialogState({
                errors: { ...errors, sectionNames: 'Section Name is required' },
            })
            return
        }
        setProductDialogState({
            sectionNames: storedValue,
            model: '',
            productName: '',
            productType: '',
            errors: { ...errors, sectionNames: '' },
        })
        setSearchText(storedValue)
        return true
    }, [searchText, errors, client, type])

    const handleChange = useCallback(
        (_, value) => {
            let tempValue = value?.replace('(+) ', '')

            setSearchText(tempValue)
            setProductDialogState({
                sectionNames: tempValue,
                model: '',
                productName: '',
                productType: '',
                productTypeRadio: RadioType.Existing,
                errors: { ...errors, sectionNames: '' },
            })
        },
        [setProductDialogState, setSearchText, errors]
    )

    const handleInputChange = useCallback(
        (e: ChangeEvent<{}>, value: string, reason?: string) => {
            if (e?.type === 'clear' || reason === 'reset') {
                setSearchText('')
                if (e?.type === 'clear') callAutoSuggestQuery('')
                return
            }

            if (e?.type === 'change') {
                const tempValue = value?.replace('(+) ', '')
                setSearchText(tempValue ?? '')
                setProductDialogState({
                    sectionNames: '',
                    model: '',
                    productName: '',
                    productType: '',
                    errors: { ...errors, sectionNames: '' },
                })
                // Query by section number (first token) so existing sections are returned
                const firstToken = (tempValue ?? '').split(/[\s-]/)[0]
                debouncedCallAutoSuggestQuery(
                    stripDoubleSpacesTrimStart(firstToken)
                )
            }
        },
        [
            setProductDialogState,
            errors,
            debouncedCallAutoSuggestQuery,
            stripDoubleSpacesTrimStart,
        ]
    )

    const handleOpen = useCallback(() => {
        setIsOpen(true)
        callAutoSuggestQuery(stripDoubleSpacesTrimStart(sectionNames) ?? '')
    }, [callAutoSuggestQuery, sectionNames, stripDoubleSpacesTrimStart])

    const handleClose = useCallback(() => setIsOpen(false), [])

    const renderFooter = useCallback(
        () => (
            <Typography
                role="button"
                tabIndex={0}
                data-testid="addNewFamilyButton"
                className={classes.addNewOption}
                onMouseDown={async (e) => {
                    e.preventDefault()
                    const created = await handleCreateNewFamily()
                    if (created) {
                        ;(document.activeElement as HTMLElement)?.blur()
                    } else {
                        setIsOpen(false)
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleCreateNewFamily()
                    }
                }}
            >
                + Create New Family
            </Typography>
        ),
        [classes.addNewOption, handleCreateNewFamily]
    )

    return (
        <GridItem
            className={clsx(classes.rowContainer, 'rowContainer')}
            xs={12}
            data-testid="sectionNames"
        >
            <AutoCompleteInput
                disabled={!firmName}
                clearOnBlur={true}
                name="sectionNames"
                label="Family"
                valid={!errors.sectionNames || errors.sectionNames === ''}
                helperText={errors.sectionNames || undefined}
                open={isOpen}
                onClose={handleClose}
                handleOpen={handleOpen}
                handleChange={handleChange}
                handleInputChange={handleInputChange}
                selectedValue={sectionNames}
                loading={autoSuggestLoading}
                items={sectionAutoSuggestOptions}
                getOptionSelected={(option, value) => option === value}
                renderFooter={
                    isBadgelessCardsEnabled ? renderFooter : undefined
                }
            />
        </GridItem>
    )
}
export default ExistingFamilyAutoCompleteInput
